// Ordinary typed client events/requests. Native SDK types are translated at the transport boundary.
// Scalar widths/defaults intentionally preserve the existing session contract; no binary envelope.

export interface ItemStack {
  item: number;
  count: number;
  bound: boolean;
}

export interface Connect {
  key: string;
  protocol: number;
  contentHash: number;
}

export interface Ping {
  token: number;
  roundTripMs: number;
}

export interface CreateCharacter {
  name: string;
  appearance: number;
}

export interface SelectCharacter {
  name: string;
}

export interface Input {
  epoch: number;
  newestSequence: number;
  buttons: Uint8Array;
}

export interface MapRequest {
  epoch: number;
}

export interface ChatSend {
  text: string;
}

export interface InventoryMove {
  fromContainer: number;
  fromIndex: number;
  toContainer: number;
  toIndex: number;
}

export interface InventoryQuickMove {
  container: number;
  index: number;
}

export interface DestroyItem {
  container: number;
  index: number;
  item: number;
}

export interface UseItem {
  container: number;
  index: number;
  item: number;
}

export interface DropItem {
  container: number;
  index: number;
  count: number;
}

export interface GatherEntity {
  epoch: number;
  placementId: string;
}

export interface InteractStart {
  epoch: number;
  x: number;
  y: number;
}

export interface InteractCancel {
  epoch: number;
}

/** Dead where you fell: leave the corpse and rise as a ghost at the nearest graveyard. */
export interface ReleaseCorpse {
  epoch: number;
}

/** A ghost by its corpse: start rekindling (hold E; interactCancel stops it). */
export interface Rekindle {
  epoch: number;
}

export interface Craft {
  recipe: number;
  count: number;
  station: number;
}

export interface BagTake {
  bag: number;
  slot: number;
  expectedItem: number;
}

export interface BagTakeAll {
  bag: number;
}

export interface BagDeposit {
  bag: number;
  container: number;
  index: number;
}

export interface Attack {
  epoch: number;
  angle: number;
  viewTick: number;
  /** How far away the cursor is (a healing circle lands there, up to its reach). */
  distance: number;
}

export interface Block {
  epoch: number;
  on: boolean;
}

export interface AdminCommand {
  action: number;
  arg: number;
  count: number;
  x: number;
  y: number;
  /** A character or map key, for the actions that take one. */
  name: string;
}

export interface PartyAction {
  action: number;
  name: string;
}

export interface IgnoreAction {
  ignore: boolean;
  name: string;
}

export interface FriendAction {
  add: boolean;
  name: string;
}

export interface TalkNpc {
  epoch: number;
  tileEntity: number;
}

export interface QuestAction {
  quest: number;
  action: number;
}

export interface UsePortal {
  epoch: number;
  tileEntity: number;
}

export interface TalentAction {
  node: number;
  refund: boolean;
}

export interface VendorAction {
  epoch: number;
  npc: number;
  action: number;
  container: number;
  index: number;
  item: number;
  count: number;
}

export interface TradeAction {
  action: number;
  name: string;
  offer?: TradeOffer;
  revision: number;
}

export interface TradeOffer {
  slots: TradeSlot[];
  copper: bigint;
}

export interface TradeSlot {
  slot: number;
  item: number;
  count: number;
}

export interface PlacedEntityState {
  epoch: number;
  id: string;
  definition: number;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  depleted: boolean;
  removed: boolean;
}

export interface Disconnect {
  code: number;
  message: string;
}

export interface Pong {
  token: number;
}

export interface ServerHello {
  serverId: string;
  serverName: string;
  maxCharacters: number;
}

export interface ContentData {
  bundle: Uint8Array;
}

export interface CharacterList {
  characters: CharacterSummary[];
}

export interface CharacterSummary {
  name: string;
  appearance: number;
  lastPlayed: string;
}

export interface CharacterResult {
  error: string;
}

export interface Welcome {
  entityId: number;
  tickRate: number;
  serverTick: number;
  serverName: string;
  motd: string;
  maxPlayers: number;
  characterName: string;
  appearance: number;
}

export interface ZoneChange {
  epoch: number;
  zoneId: number;
  instanceId: number;
  zoneName: string;
  widthTiles: number;
  heightTiles: number;
  flags: number;
  x: number;
  y: number;
  facing: number;
  serverTick: number;
  terrainHash: bigint;
  time?: TimeOfDay;
  lights: ZoneLight[];
}

/** A point light placed on the map: world pixels, radius in pixels, colour 0xrrggbb. */
export interface ZoneLight {
  x: number;
  y: number;
  radius: number;
  color: number;
  flicker: boolean;
}

export interface TimeOfDay {
  time: number;
  daySeconds: number;
}

export interface MapData {
  epoch: number;
  terrain: Uint8Array;
}

export interface ChunkData {
  epoch: number;
  x: number;
  y: number;
  indices: number[];
  objects: number[];
}

export interface ChunkUnload {
  epoch: number;
  x: number;
  y: number;
}

export interface TileUpdate {
  epoch: number;
  x: number;
  y: number;
  ground: number;
  object: number;
}

export interface EntitySpawn {
  epoch: number;
  entityId: number;
  kind: number;
  name: string;
  appearance: number;
  level: number;
  armor: number[];
  x: number;
  y: number;
  facing: number;
  flags: number;
}

export interface EntityDespawn {
  epoch: number;
  entityId: number;
}

export interface EntityEquipment {
  epoch: number;
  entityId: number;
  armor: number[];
}

export interface Snapshot {
  epoch: number;
  tick: number;
  lastProcessedInput: number;
  self?: EntityState;
  entities: EntityState[];
}

export interface EntityState {
  entityId: number;
  x: number;
  y: number;
  facingFlags: number;
  healthPct: number;
  stamina: number;
  staminaDelay: number;
  stun: number;
}

export interface CombatEvent {
  epoch: number;
  type: number;
  entity: number;
  other: number;
  value: number;
}

export interface PlayerStats {
  health: number;
  maxHealth: number;
  level: number;
  exp: number;
  money: bigint;
  mana: number;
  maxMana: number;
}

/** Where the player stands through death (LifeState) and, as a ghost, where its corpse lies. */
export interface LifeState {
  state: number;
  corpseMap: string;
  corpseX: number;
  corpseY: number;
}

/** A player's corpse in the instance (from the link's corpse rows; removed: it's gone). */
export interface CorpseState {
  epoch: number;
  entityId: number;
  x: number;
  y: number;
  appearance: number;
  facing: number;
  removed: boolean;
}

/** A buff or debuff on a player in the instance (from the link's buff rows; removed: it's gone). */
export interface BuffState {
  epoch: number;
  id: number;
  entityId: number;
  kind: number;
  startTick: number;
  endTick: number;
  removed: boolean;
}

/** A healing circle in the instance (from the link's heal_zone rows; removed: it's gone). */
export interface HealZoneState {
  epoch: number;
  id: number;
  x: number;
  y: number;
  item: number;
  startTick: number;
  endTick: number;
  removed: boolean;
}

export interface Talents {
  allocated: number[];
  auras: number[];
}

export interface InventorySync {
  main: ItemStack[];
  belt: ItemStack[];
  armor: ItemStack[];
}

export interface HarvestState {
  epoch: number;
  phase: number;
  x: number;
  y: number;
  durationTicks: number;
}

export interface TileEntitySpawn {
  epoch: number;
  id: number;
  type: number;
  x: number;
  y: number;
  data: number;
  label: string;
}

export interface TileEntityDespawn {
  epoch: number;
  id: number;
}

export interface LootBagSpawn {
  epoch: number;
  id: number;
  x: number;
  y: number;
  contents: ItemStack[];
}

export interface LootBagDespawn {
  epoch: number;
  id: number;
  reason: number;
}

export interface Notice {
  kind: number;
  text: string;
}

export interface Chat {
  channel: number;
  from: string;
  text: string;
}

export interface PlayerList {
  maxPlayers: number;
  entries: PlayerListEntry[];
}

export interface PlayerListEntry {
  name: string;
  zoneName: string;
  appearance: number;
  inMyParty: boolean;
  sameMap: boolean;
}

export interface AdminMap {
  key: string;
  name: string;
}

export interface AdminStatus {
  isAdmin: boolean;
  godMode: boolean;
  maps: AdminMap[];
}

export interface PartyState {
  leader: string;
  maxSize: number;
  members: PartyMember[];
}

export interface PartyMember {
  name: string;
  entityId: number;
  appearance: number;
  healthPct: number;
  zoneName: string;
  online: boolean;
}

export interface PartyInvite {
  inviter: string;
  expiresIn: number;
}

export interface PartyPositions {
  members: PartyPosition[];
}

export interface PartyPosition {
  name: string;
  zoneId: number;
  instanceId: number;
  x: number;
  y: number;
}

export interface IgnoreList {
  names: string[];
}

export interface FriendList {
  friends: Friend[];
}

export interface Friend {
  name: string;
  online: boolean;
  zoneName: string;
  level: number;
}

export interface NpcDialogue {
  npc: number;
  options: QuestOption[];
}

export interface QuestOption {
  quest: number;
  status: number;
}

export interface QuestLog {
  active: ActiveQuest[];
  completed: number[];
}

export interface ActiveQuest {
  quest: number;
  progress: number[];
}

export interface TradeInvite {
  from: string;
  expiresIn: number;
}

export interface TradeState {
  view?: TradeView;
}

export interface TradeView {
  partner: string;
  revision: number;
  mine?: TradeOffer;
  mineAccepted: boolean;
  theirs: ItemStack[];
  theirCopper: bigint;
  theirsAccepted: boolean;
}

export type ClientKind =
  | { case: 'connect'; value: Connect }
  | { case: 'ping'; value: Ping }
  | { case: 'createCharacter'; value: CreateCharacter }
  | { case: 'selectCharacter'; value: SelectCharacter }
  | { case: 'input'; value: Input }
  | { case: 'mapRequest'; value: MapRequest }
  | { case: 'chatSend'; value: ChatSend }
  | { case: 'inventoryMove'; value: InventoryMove }
  | { case: 'inventoryQuickMove'; value: InventoryQuickMove }
  | { case: 'destroyItem'; value: DestroyItem }
  | { case: 'useItem'; value: UseItem }
  | { case: 'dropItem'; value: DropItem }
  | { case: 'interactStart'; value: InteractStart }
  | { case: 'interactCancel'; value: InteractCancel }
  | { case: 'releaseCorpse'; value: ReleaseCorpse }
  | { case: 'rekindle'; value: Rekindle }
  | { case: 'craft'; value: Craft }
  | { case: 'bagTake'; value: BagTake }
  | { case: 'bagTakeAll'; value: BagTakeAll }
  | { case: 'bagDeposit'; value: BagDeposit }
  | { case: 'attack'; value: Attack }
  | { case: 'block'; value: Block }
  | { case: 'adminCommand'; value: AdminCommand }
  | { case: 'partyAction'; value: PartyAction }
  | { case: 'ignoreAction'; value: IgnoreAction }
  | { case: 'talkNpc'; value: TalkNpc }
  | { case: 'questAction'; value: QuestAction }
  | { case: 'usePortal'; value: UsePortal }
  | { case: 'talentAction'; value: TalentAction }
  | { case: 'vendorAction'; value: VendorAction }
  | { case: 'tradeAction'; value: TradeAction }
  | { case: 'friendAction'; value: FriendAction }
  | { case: 'gatherEntity'; value: GatherEntity };
export interface WorldClientMessage { kind: ClientKind }
export type ClientKindInit = ClientKind extends infer K ? K extends { case: infer C; value: infer V } ? { case: C; value: Init<V> } : never : never;

export type ServerKind =
  | { case: 'disconnect'; value: Disconnect }
  | { case: 'pong'; value: Pong }
  | { case: 'serverHello'; value: ServerHello }
  | { case: 'contentData'; value: ContentData }
  | { case: 'characterList'; value: CharacterList }
  | { case: 'characterResult'; value: CharacterResult }
  | { case: 'welcome'; value: Welcome }
  | { case: 'zoneChange'; value: ZoneChange }
  | { case: 'mapData'; value: MapData }
  | { case: 'chunkData'; value: ChunkData }
  | { case: 'chunkUnload'; value: ChunkUnload }
  | { case: 'tileUpdate'; value: TileUpdate }
  | { case: 'entitySpawn'; value: EntitySpawn }
  | { case: 'entityDespawn'; value: EntityDespawn }
  | { case: 'entityEquipment'; value: EntityEquipment }
  | { case: 'snapshot'; value: Snapshot }
  | { case: 'combatEvent'; value: CombatEvent }
  | { case: 'playerStats'; value: PlayerStats }
  | { case: 'inventorySync'; value: InventorySync }
  | { case: 'harvestState'; value: HarvestState }
  | { case: 'tileEntitySpawn'; value: TileEntitySpawn }
  | { case: 'tileEntityDespawn'; value: TileEntityDespawn }
  | { case: 'lootBagSpawn'; value: LootBagSpawn }
  | { case: 'lootBagDespawn'; value: LootBagDespawn }
  | { case: 'notice'; value: Notice }
  | { case: 'chat'; value: Chat }
  | { case: 'playerList'; value: PlayerList }
  | { case: 'adminStatus'; value: AdminStatus }
  | { case: 'partyState'; value: PartyState }
  | { case: 'partyInvite'; value: PartyInvite }
  | { case: 'partyPositions'; value: PartyPositions }
  | { case: 'ignoreList'; value: IgnoreList }
  | { case: 'npcDialogue'; value: NpcDialogue }
  | { case: 'questLog'; value: QuestLog }
  | { case: 'tradeInvite'; value: TradeInvite }
  | { case: 'tradeState'; value: TradeState }
  | { case: 'friendList'; value: FriendList }
  | { case: 'timeOfDay'; value: TimeOfDay }
  | { case: 'placedEntityState'; value: PlacedEntityState }
  | { case: 'talents'; value: Talents }
  | { case: 'healZoneState'; value: HealZoneState }
  | { case: 'lifeState'; value: LifeState }
  | { case: 'corpseState'; value: CorpseState }
  | { case: 'buffState'; value: BuffState };
export interface WorldServerMessage { kind: ServerKind }
export type ServerKindInit = ServerKind extends infer K ? K extends { case: infer C; value: infer V } ? { case: C; value: Init<V> } : never : never;

export type Init<T> = T extends Uint8Array ? T : T extends (infer E)[] ? Init<E>[] : T extends object ? { [K in keyof T]?: Init<T[K]> } : T;

const models: Record<string, Record<string, string>> = {
  "ItemStack": {
    "item": "uint32",
    "count": "uint32",
    "bound": "bool"
  },
  "Connect": {
    "key": "string",
    "protocol": "uint32",
    "contentHash": "uint32"
  },
  "Ping": {
    "token": "uint32",
    "roundTripMs": "uint32"
  },
  "CreateCharacter": {
    "name": "string",
    "appearance": "uint32"
  },
  "SelectCharacter": {
    "name": "string"
  },
  "Input": {
    "epoch": "uint32",
    "newestSequence": "uint32",
    "buttons": "bytes"
  },
  "MapRequest": {
    "epoch": "uint32"
  },
  "ChatSend": {
    "text": "string"
  },
  "InventoryMove": {
    "fromContainer": "uint32",
    "fromIndex": "uint32",
    "toContainer": "uint32",
    "toIndex": "uint32"
  },
  "InventoryQuickMove": {
    "container": "uint32",
    "index": "uint32"
  },
  "DestroyItem": {
    "container": "uint32",
    "index": "uint32",
    "item": "uint32"
  },
  "UseItem": {
    "container": "uint32",
    "index": "uint32",
    "item": "uint32"
  },
  "DropItem": {
    "container": "uint32",
    "index": "uint32",
    "count": "uint32"
  },
  "GatherEntity": {
    "epoch": "uint32",
    "placementId": "string"
  },
  "InteractStart": {
    "epoch": "uint32",
    "x": "sint32",
    "y": "sint32"
  },
  "InteractCancel": {
    "epoch": "uint32"
  },
  "ReleaseCorpse": {
    "epoch": "uint32"
  },
  "Rekindle": {
    "epoch": "uint32"
  },
  "Craft": {
    "recipe": "uint32",
    "count": "uint32",
    "station": "uint32"
  },
  "BagTake": {
    "bag": "uint32",
    "slot": "uint32",
    "expectedItem": "uint32"
  },
  "BagTakeAll": {
    "bag": "uint32"
  },
  "BagDeposit": {
    "bag": "uint32",
    "container": "uint32",
    "index": "uint32"
  },
  "Attack": {
    "epoch": "uint32",
    "angle": "f32",
    "viewTick": "uint32",
    "distance": "f32"
  },
  "Block": {
    "epoch": "uint32",
    "on": "bool"
  },
  "AdminCommand": {
    "action": "uint32",
    "arg": "uint32",
    "count": "sint32",
    "x": "f32",
    "y": "f32",
    "name": "string"
  },
  "PartyAction": {
    "action": "uint32",
    "name": "string"
  },
  "IgnoreAction": {
    "ignore": "bool",
    "name": "string"
  },
  "FriendAction": {
    "add": "bool",
    "name": "string"
  },
  "TalkNpc": {
    "epoch": "uint32",
    "tileEntity": "uint32"
  },
  "QuestAction": {
    "quest": "uint32",
    "action": "uint32"
  },
  "UsePortal": {
    "epoch": "uint32",
    "tileEntity": "uint32"
  },
  "TalentAction": {
    "node": "uint32",
    "refund": "bool"
  },
  "VendorAction": {
    "epoch": "uint32",
    "npc": "uint32",
    "action": "uint32",
    "container": "uint32",
    "index": "uint32",
    "item": "uint32",
    "count": "uint32"
  },
  "TradeAction": {
    "action": "uint32",
    "name": "string",
    "offer": "TradeOffer",
    "revision": "uint32"
  },
  "TradeOffer": {
    "slots": "[]TradeSlot",
    "copper": "int64"
  },
  "TradeSlot": {
    "slot": "uint32",
    "item": "uint32",
    "count": "uint32"
  },
  "PlacedEntityState": {
    "epoch": "uint32",
    "id": "string",
    "definition": "uint32",
    "x": "sint32",
    "y": "sint32",
    "offsetX": "sint32",
    "offsetY": "sint32",
    "depleted": "bool",
    "removed": "bool"
  },
  "Disconnect": {
    "code": "uint32",
    "message": "string"
  },
  "Pong": {
    "token": "uint32"
  },
  "ServerHello": {
    "serverId": "string",
    "serverName": "string",
    "maxCharacters": "uint32"
  },
  "ContentData": {
    "bundle": "bytes"
  },
  "CharacterList": {
    "characters": "[]CharacterSummary"
  },
  "CharacterSummary": {
    "name": "string",
    "appearance": "uint32",
    "lastPlayed": "string"
  },
  "CharacterResult": {
    "error": "string"
  },
  "Welcome": {
    "entityId": "uint32",
    "tickRate": "uint32",
    "serverTick": "uint32",
    "serverName": "string",
    "motd": "string",
    "maxPlayers": "uint32",
    "characterName": "string",
    "appearance": "uint32"
  },
  "ZoneChange": {
    "epoch": "uint32",
    "zoneId": "uint32",
    "instanceId": "uint32",
    "zoneName": "string",
    "widthTiles": "uint32",
    "heightTiles": "uint32",
    "flags": "uint32",
    "x": "sint32",
    "y": "sint32",
    "facing": "uint32",
    "serverTick": "uint32",
    "terrainHash": "fixed64",
    "time": "TimeOfDay",
    "lights": "[]ZoneLight"
  },
  "ZoneLight": {
    "x": "sint32",
    "y": "sint32",
    "radius": "uint32",
    "color": "uint32",
    "flicker": "bool"
  },
  "TimeOfDay": {
    "time": "f32",
    "daySeconds": "uint32"
  },
  "MapData": {
    "epoch": "uint32",
    "terrain": "bytes"
  },
  "ChunkData": {
    "epoch": "uint32",
    "x": "sint32",
    "y": "sint32",
    "indices": "[]uint32",
    "objects": "[]uint32"
  },
  "ChunkUnload": {
    "epoch": "uint32",
    "x": "sint32",
    "y": "sint32"
  },
  "TileUpdate": {
    "epoch": "uint32",
    "x": "sint32",
    "y": "sint32",
    "ground": "uint32",
    "object": "uint32"
  },
  "EntitySpawn": {
    "epoch": "uint32",
    "entityId": "uint32",
    "kind": "uint32",
    "name": "string",
    "appearance": "uint32",
    "level": "uint32",
    "armor": "[]uint32",
    "x": "sint32",
    "y": "sint32",
    "facing": "uint32",
    "flags": "uint32"
  },
  "EntityDespawn": {
    "epoch": "uint32",
    "entityId": "uint32"
  },
  "EntityEquipment": {
    "epoch": "uint32",
    "entityId": "uint32",
    "armor": "[]uint32"
  },
  "Snapshot": {
    "epoch": "uint32",
    "tick": "uint32",
    "lastProcessedInput": "uint32",
    "self": "EntityState",
    "entities": "[]EntityState"
  },
  "EntityState": {
    "entityId": "uint32",
    "x": "sint32",
    "y": "sint32",
    "facingFlags": "uint32",
    "healthPct": "uint32",
    "stamina": "uint32",
    "staminaDelay": "uint32",
    "stun": "uint32"
  },
  "CombatEvent": {
    "epoch": "uint32",
    "type": "uint32",
    "entity": "uint32",
    "other": "uint32",
    "value": "f32"
  },
  "PlayerStats": {
    "health": "uint32",
    "maxHealth": "uint32",
    "level": "uint32",
    "exp": "uint32",
    "money": "int64",
    "mana": "uint32",
    "maxMana": "uint32"
  },
  "LifeState": {
    "state": "uint32",
    "corpseMap": "string",
    "corpseX": "f32",
    "corpseY": "f32"
  },
  "CorpseState": {
    "epoch": "uint32",
    "entityId": "uint32",
    "x": "f32",
    "y": "f32",
    "appearance": "uint32",
    "facing": "uint32",
    "removed": "bool"
  },
  "BuffState": {
    "epoch": "uint32",
    "id": "uint32",
    "entityId": "uint32",
    "kind": "uint32",
    "startTick": "uint32",
    "endTick": "uint32",
    "removed": "bool"
  },
  "HealZoneState": {
    "epoch": "uint32",
    "id": "uint32",
    "x": "f32",
    "y": "f32",
    "item": "uint32",
    "startTick": "uint32",
    "endTick": "uint32",
    "removed": "bool"
  },
  "Talents": {
    "allocated": "[]uint32",
    "auras": "[]uint32"
  },
  "InventorySync": {
    "main": "[]ItemStack",
    "belt": "[]ItemStack",
    "armor": "[]ItemStack"
  },
  "HarvestState": {
    "epoch": "uint32",
    "phase": "uint32",
    "x": "sint32",
    "y": "sint32",
    "durationTicks": "uint32"
  },
  "TileEntitySpawn": {
    "epoch": "uint32",
    "id": "uint32",
    "type": "uint32",
    "x": "sint32",
    "y": "sint32",
    "data": "uint32",
    "label": "string"
  },
  "TileEntityDespawn": {
    "epoch": "uint32",
    "id": "uint32"
  },
  "LootBagSpawn": {
    "epoch": "uint32",
    "id": "uint32",
    "x": "f32",
    "y": "f32",
    "contents": "[]ItemStack"
  },
  "LootBagDespawn": {
    "epoch": "uint32",
    "id": "uint32",
    "reason": "uint32"
  },
  "Notice": {
    "kind": "uint32",
    "text": "string"
  },
  "Chat": {
    "channel": "uint32",
    "from": "string",
    "text": "string"
  },
  "PlayerList": {
    "maxPlayers": "uint32",
    "entries": "[]PlayerListEntry"
  },
  "PlayerListEntry": {
    "name": "string",
    "zoneName": "string",
    "appearance": "uint32",
    "inMyParty": "bool",
    "sameMap": "bool"
  },
  "AdminMap": {
    "key": "string",
    "name": "string"
  },
  "AdminStatus": {
    "isAdmin": "bool",
    "godMode": "bool",
    "maps": "[]AdminMap"
  },
  "PartyState": {
    "leader": "string",
    "maxSize": "uint32",
    "members": "[]PartyMember"
  },
  "PartyMember": {
    "name": "string",
    "entityId": "uint32",
    "appearance": "uint32",
    "healthPct": "uint32",
    "zoneName": "string",
    "online": "bool"
  },
  "PartyInvite": {
    "inviter": "string",
    "expiresIn": "f32"
  },
  "PartyPositions": {
    "members": "[]PartyPosition"
  },
  "PartyPosition": {
    "name": "string",
    "zoneId": "uint32",
    "instanceId": "uint32",
    "x": "f32",
    "y": "f32"
  },
  "IgnoreList": {
    "names": "[]string"
  },
  "FriendList": {
    "friends": "[]Friend"
  },
  "Friend": {
    "name": "string",
    "online": "bool",
    "zoneName": "string",
    "level": "uint32"
  },
  "NpcDialogue": {
    "npc": "uint32",
    "options": "[]QuestOption"
  },
  "QuestOption": {
    "quest": "uint32",
    "status": "uint32"
  },
  "QuestLog": {
    "active": "[]ActiveQuest",
    "completed": "[]uint32"
  },
  "ActiveQuest": {
    "quest": "uint32",
    "progress": "[]uint32"
  },
  "TradeInvite": {
    "from": "string",
    "expiresIn": "f32"
  },
  "TradeState": {
    "view": "TradeView"
  },
  "TradeView": {
    "partner": "string",
    "revision": "uint32",
    "mine": "TradeOffer",
    "mineAccepted": "bool",
    "theirs": "[]ItemStack",
    "theirCopper": "int64",
    "theirsAccepted": "bool"
  }
};
const clientModels: Record<string, string> = {
  "connect": "Connect",
  "ping": "Ping",
  "createCharacter": "CreateCharacter",
  "selectCharacter": "SelectCharacter",
  "input": "Input",
  "mapRequest": "MapRequest",
  "chatSend": "ChatSend",
  "inventoryMove": "InventoryMove",
  "inventoryQuickMove": "InventoryQuickMove",
  "destroyItem": "DestroyItem",
  "useItem": "UseItem",
  "dropItem": "DropItem",
  "interactStart": "InteractStart",
  "interactCancel": "InteractCancel",
  "releaseCorpse": "ReleaseCorpse",
  "rekindle": "Rekindle",
  "craft": "Craft",
  "bagTake": "BagTake",
  "bagTakeAll": "BagTakeAll",
  "bagDeposit": "BagDeposit",
  "attack": "Attack",
  "block": "Block",
  "adminCommand": "AdminCommand",
  "partyAction": "PartyAction",
  "ignoreAction": "IgnoreAction",
  "talkNpc": "TalkNpc",
  "questAction": "QuestAction",
  "usePortal": "UsePortal",
  "talentAction": "TalentAction",
  "vendorAction": "VendorAction",
  "tradeAction": "TradeAction",
  "friendAction": "FriendAction",
  "gatherEntity": "GatherEntity"
};
const serverModels: Record<string, string> = {
  "disconnect": "Disconnect",
  "pong": "Pong",
  "serverHello": "ServerHello",
  "contentData": "ContentData",
  "characterList": "CharacterList",
  "characterResult": "CharacterResult",
  "welcome": "Welcome",
  "zoneChange": "ZoneChange",
  "mapData": "MapData",
  "chunkData": "ChunkData",
  "chunkUnload": "ChunkUnload",
  "tileUpdate": "TileUpdate",
  "entitySpawn": "EntitySpawn",
  "entityDespawn": "EntityDespawn",
  "entityEquipment": "EntityEquipment",
  "snapshot": "Snapshot",
  "combatEvent": "CombatEvent",
  "playerStats": "PlayerStats",
  "inventorySync": "InventorySync",
  "harvestState": "HarvestState",
  "tileEntitySpawn": "TileEntitySpawn",
  "tileEntityDespawn": "TileEntityDespawn",
  "lootBagSpawn": "LootBagSpawn",
  "lootBagDespawn": "LootBagDespawn",
  "notice": "Notice",
  "chat": "Chat",
  "playerList": "PlayerList",
  "adminStatus": "AdminStatus",
  "partyState": "PartyState",
  "partyInvite": "PartyInvite",
  "partyPositions": "PartyPositions",
  "ignoreList": "IgnoreList",
  "npcDialogue": "NpcDialogue",
  "questLog": "QuestLog",
  "tradeInvite": "TradeInvite",
  "tradeState": "TradeState",
  "friendList": "FriendList",
  "timeOfDay": "TimeOfDay",
  "placedEntityState": "PlacedEntityState",
  "talents": "Talents",
  "healZoneState": "HealZoneState",
  "lifeState": "LifeState",
  "corpseState": "CorpseState",
  "buffState": "BuffState"
};

function normalize(name: string, value: unknown): unknown {
  const source = (value ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [field, type] of Object.entries(models[name])) {
    const v = source[field];
    out[field] = type.startsWith('[]') ? ((v ?? []) as unknown[]).map(x => scalar(type.slice(2), x)) : scalar(type, v);
  }
  return out;
}
function scalar(type: string, value: unknown): unknown {
  if (type === 'string') return value ?? '';
  if (type === 'bool') return value ?? false;
  if (type === 'bytes') return value ?? new Uint8Array();
  if (type === 'uint64' || type === 'fixed64' || type === 'int64') return value ?? 0n;
  if (type === 'f32') return Math.fround((value ?? 0) as number);
  if (type === 'double' || type === 'uint32' || type === 'sint32' || type === 'int32') return value ?? 0;
  return value === undefined ? undefined : normalize(type, value);
}
export function serverEvent(kind: ServerKindInit): WorldServerMessage {
  return { kind: { case: kind.case, value: normalize(serverModels[kind.case], kind.value) } as ServerKind };
}
export function clientRequest(kind: ClientKindInit): WorldClientMessage {
  return { kind: { case: kind.case, value: normalize(clientModels[kind.case], kind.value) } as ClientKind };
}
